import { APP_INITIALIZER } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { CompanyService } from '../services/company.service';
import { ThemeService } from '../services/theme.service';
import { CookieService } from '../services/cookie.service';
import { ResourcesService } from '../services/resources.service';
import { language } from '../constants/constants';
import { CacheManagerService } from '~/core/services/cache-manager.service';
import { getBrowserLanguage } from '../helpers/storage-utils';
import { LayoutWidgetService } from '../services/layout-widget.service';
import { isDesktop } from '~/core/helpers/device-utils';

const SYS_CONFIG_VERSION = 'SYS_CONFIG_VERSION';

export function initCompany(
  companyService: CompanyService,
  themeService: ThemeService,
  translate: TranslateService,
  cacheManagerService: CacheManagerService,
) {
  return () => {
    return new Promise<void>(async (resolve, reject) => {
      if (window.__infoCompany && Object.keys(window.__infoCompany)?.length) {
        await setCompanySettings(window.__infoCompany, companyService, themeService, translate);
        window.dispatchEvent(new Event('theme-ready'));
        resolve();
      } else {
        companyService.info().subscribe({
          next: async (response) => {
            try {
              const currentVersion = localStorage.getItem(SYS_CONFIG_VERSION);
              const isFirstAccess = !localStorage.getItem('FORMART_ODD');
              if (response.sys_config?.platform_versions?.web) {
                if (currentVersion != null && currentVersion !== response.sys_config?.platform_versions?.web && !isFirstAccess) {
                  localStorage.setItem(SYS_CONFIG_VERSION, response.sys_config?.platform_versions?.web);
                  await cacheManagerService.clearCache();
                  // location.reload();
                } else {
                  localStorage.setItem(SYS_CONFIG_VERSION, response.sys_config?.platform_versions?.web);
                }
              } else if (!currentVersion && isFirstAccess) {
                localStorage.setItem(SYS_CONFIG_VERSION, '1.0.0');
                await cacheManagerService.clearCache();
                // location.reload();
              }
              await setCompanySettings(response, companyService, themeService, translate);
              // NOTE: Redirect logic removed - RedirectComponent now renders main page inline without URL changes
              window.dispatchEvent(new Event('theme-ready'));
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          error: (err) => reject(err),
        });
      }
    });
  };
}

async function setCompanySettings(
  companyInfo: any,
  companyService: CompanyService,
  themeService: ThemeService,
  translate: TranslateService,
) {
  const company = companyInfo?.company ?? {};
  const companyLangKey = company?.language;
  const lang =
    CookieService.getCookie('lang') || language[companyLangKey as keyof typeof language] || getBrowserLanguage() || language.PT_BR;
  translate.setDefaultLang(lang);
  // Diferir o HTTP request de tradução para não bloquear ApplicationRef.isStable e o FCP.
  // setDefaultLang já garante fallback imediato para as pipes enquanto o idle não dispara.
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => translate.use(lang), { timeout: 1000 });
  } else {
    Promise.resolve().then(() => translate.use(lang));
  }
  companyService.setCurrentCompany({
    ...companyInfo,
    company,
    config: companyInfo?.config ?? {},
    view_config: companyInfo?.view_config ?? null,
  });
  // Não bloqueia o bootstrap e nem o paint inicial do browser (Microtask delay -> Macrotask/Idle delay)
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => ResourcesService.loadDependences(company), { timeout: 2000 });
  } else {
    setTimeout(() => ResourcesService.loadDependences(company), 0);
  }
  ThemeService.checkIfAddLightClassTheme(company);
  await themeService.customTheme(company);
  _changeFavicon(company.favicon);
  const logoHeader = document.getElementById('header-logo-identifier') as HTMLImageElement;
  if (logoHeader && !logoHeader.src) {
    logoHeader.src = isDesktop() ? company.logo : company.mobile_logo;
    logoHeader.style.display = 'block';
  }
  // Pre-fetch imediato do live summary assim que os dados da companhia estão prontos
  // layoutWidgetService.liveSummary(false, true);
}

function _changeFavicon(src: string) {
  if (!src) return;

  let faviconLink = document.querySelector<HTMLLinkElement>('#app-favicon');
  if (!faviconLink) {
    faviconLink = document.createElement('link');
    faviconLink.id = 'app-favicon';
    faviconLink.rel = 'icon';
    document.head.appendChild(faviconLink);
  }

  // `link.href` é sempre URL absoluta; `src` da API pode ser relativo ou protocol-relative.
  // Comparar só strings cruas fazia mismatch → setava `href` de novo → novo download no mesmo recurso.
  let resolved: string;
  try {
    resolved = new URL(src, document.baseURI).href;
  } catch {
    resolved = src;
  }

  if (faviconLink.href !== resolved) {
    faviconLink.href = src;
  }
}

export const provideInitCompany = () => {
  return {
    provide: APP_INITIALIZER,
    useFactory: initCompany,
    deps: [CompanyService, ThemeService, TranslateService, CacheManagerService, LayoutWidgetService],
    multi: true,
  };
};
